import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import React, { FC, useEffect } from 'react';
import { Sentry } from '@idk-web/core-utils';
import {
  useAsync,
  AsyncState,
  Styling,
  Box,
  Typography,
  Bold,
  AlternativeButton,
  useDialogs,
  ErrorDialog,
} from '@idk-web/core-ui';
import { getInboxThread } from '@idk-web/api';
import { translateError } from '@/utils/error';

const Card = styled(Box).attrs(({ theme }) => ({
  direction: 'vertical',
  spacing: theme.spacing[4],
}))`
  width: 100%;
  padding: ${Styling.spacing(4)};
`;

const MessageBlock = styled(Box).attrs({ direction: 'vertical', spacing: 8 })`
  width: 100%;
  padding: ${Styling.spacing(4)};
  border: 1px solid ${({ theme }) => theme.palette.grayscale.light2};
  border-radius: 8px;
`;

type InboxThreadCardProps = {
  messageId: string;
  companyName: string;
  onBack(): void;
};

const InboxThreadCard: FC<InboxThreadCardProps> = ({
  messageId,
  companyName,
  onBack,
}) => {
  const { t } = useTranslation();
  const dialog = useDialogs();
  const thread = useAsync(
    (signal) => getInboxThread(messageId, signal),
    [messageId],
  );

  useEffect(() => {
    if (thread.state === AsyncState.ERROR) {
      Sentry.reportError('Failed to fetch inbox thread', {
        error: thread.error,
      });
      dialog.open((props) => (
        <ErrorDialog {...props} text={translateError(t, thread.error)} />
      ));
    }
  }, [thread]);

  return (
    <Card>
      <Box alignX="space-between" alignY="center" style={{ width: '100%' }}>
        <Typography type="h3">
          {thread.value?.subject ?? t('inbox.safemail.thread.title')}
        </Typography>
        <AlternativeButton
          text={t('inbox.safemail.thread.back')}
          onClick={onBack}
        />
      </Box>

      {thread.value?.messages.length === 0 && (
        <Typography type="body">{t('inbox.safemail.thread.empty')}</Typography>
      )}

      {thread.value?.messages.map((message) => (
        <MessageBlock key={message.id}>
          <Box alignX="space-between" alignY="center" style={{ width: '100%' }}>
            <Bold>
              {message.direction === 'FROM_USER'
                ? t('inbox.safemail.thread.from_you')
                : t('inbox.safemail.thread.from_company', {
                    company: companyName,
                  })}
            </Bold>
            <Typography type="caption">
              {new Date(message.datetime * 1000).toLocaleString()}
            </Typography>
          </Box>
          <Typography type="body" style={{ whiteSpace: 'break-spaces' }}>
            <span dangerouslySetInnerHTML={{ __html: message.body }} />
          </Typography>
          {message.attachments.length > 0 && (
            <Typography type="caption">
              {t('inbox.safemail.thread.attachments')}{' '}
              {message.attachments.map((a) => a.name).join(', ')}
            </Typography>
          )}
        </MessageBlock>
      ))}
    </Card>
  );
};

export default InboxThreadCard;
